import { ChangeDetectionStrategy, Component, computed, effect, signal, ViewEncapsulation } from '@angular/core';
import { PlotLineOptions, PlotOptions, PointInteractionEventObject, Point, SeriesLineOptions, Series } from 'highcharts';
import { PbiChartTypes } from '../../../shared/chart/chart';
import { filterToDistinct, group } from '../../../shared/utils';
import { VisualFieldRoleType } from '../VisualFieldRoleType';
import { VisualFieldRoleTypeMap } from '../VisualFieldRoleTypeMap';
import { FontSize } from '../VisualStyle';
import { ChartSeries, ChartVisual, ChartVisualComponent, CreateSeries } from './ChartVisual.component';
import { debounced } from '../../utils/debounced';
import { SortDirection } from 'src/app/services/api.services';
import { VisualField, VisualFieldSortingInit } from '../VisualField';

@Component({
  selector: 'pbir-visual__lineChart',
  templateUrl: './ChartVisual.component.html',
  styleUrls: ['./ChartVisual.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class LineChartVisualComponent
  extends ChartVisualComponent<
    SeriesLineOptions,
    LineChartVisual.Options,
    LineChartVisual.State,
    LineChartVisual.Style> {
  get measuresRoleType() { return LineChartVisual.roles.measures; }
  get groupingRoleType() { return LineChartVisual.roles.axis; }
  get legendRoleType() { return LineChartVisual.roles.legend; }

  get chartType() { return 'line'; }
  isHorizontalPlot() { return false; }
  areDataLabelsInside() { return false; }
  computeShowNameInDataLabel() { return false; }
  getAdditionalRightSpacing() { return 0; }

  computePlotOptions(): PlotOptions {
    let plotOptions = super.computePlotOptions();

    let chartOptions: Partial<PlotLineOptions> = {
      animation: !this.disableAnimations,
      inactiveOtherPoints: true,
      getExtremesFromAll: true,
      nullInteraction: false,
    };

    return {
      ...plotOptions,
      line: {
        ...chartOptions,
        allowPointSelect: true,
        zoneAxis: 'x',
      }
    };
  }

  private readonly zonesVersion = signal(0);

  private redrawZones() {
    this.zonesVersion.update(x => x + 1);
  }

  readonly zonesPerSeries = effect(() => {
    let _ = [this.zonesVersion(), this.series()];

    let hc = this.chart()?.chart();
    if (!hc)
      return;

    this.zoneRedraw = true;

    try {
      this.updateZonesFromSelection();
    }
    catch (err) {
      this.zoneRedraw = false;
      throw err;
    }
  });

  private zoneRedraw = false;

  private readonly updateZonesFromSelection = debounced(() => {
    let hc = this.chart()?.chart();
    if (!hc)
      return;
      
    this.zoneRedraw = true;

    try {
      let anySelection = !!hc.getSelectedPoints()?.length;

      let inactiveSeriesZones = anySelection ? [{
        value: Number.MAX_SAFE_INTEGER,
        dashStyle: 'Dot',
      }] : [];

      let newZonesBySeries = new Map<Series, any[]>();
      let activeSeries = new Set<Series>();

      if (anySelection) {
        for (let series of hc.series) {
          let allPoints = [...series.points].sort((a, b) => a.x - b.x);

          let pointSet: Point[] = null;
          let pointSets: Point[][] = [];

          for (let i = 0; i < allPoints.length; ++i) {
            let p = allPoints[i];
            if (p.selected) {
              if (pointSet)
                pointSet[1] = p;
              else
                pointSet = [p];
            }
            else {
              if (pointSet) {
                pointSets.push(pointSet);
                pointSet = null;
              }
            }
          }

          if (pointSet)
            pointSets.push(pointSet);

          if (!pointSets.length) {
            newZonesBySeries.set(series, inactiveSeriesZones);
            continue;
          }

          let zones: Highcharts.SeriesZonesOptionsObject[] = [];

          for (let [start, end] of pointSets) {
            end ??= start;

            zones.push({
              value: start.x - 0.001,
              dashStyle: 'Dot',
            });

            zones.push({
              value: end.x,
              dashStyle: 'Solid',
            });
          }

          zones.push({
            value: Number.MAX_SAFE_INTEGER,
            dashStyle: 'Dot',
          });

          newZonesBySeries.set(series, zones);
          activeSeries.add(series);
        }
      }

      for (let series of hc.series) {
        if (!newZonesBySeries.has(series))
          newZonesBySeries.set(series, inactiveSeriesZones);

        let currentZones = (series.options as any).zones;
        let newZones = newZonesBySeries.get(series) ?? [];

        if (!currentZones?.length && !newZones.length)
          continue;

        if (currentZones && newZones.length === currentZones.length) {
          let changed = false;

          for (let i = 0; i < newZones.length; ++i) {
            let a = newZones[i];
            let b = currentZones[i];
            if (a.value != b.value || a.dashStyle != b.dashStyle) {
              changed = true;
              break;
            }
          }

          if (!changed)
            continue;
        }

        series.update(<any>{ zones: newZones }, false);
      }

      hc.redraw();
    }
    finally {
      this.zoneRedraw = false;
    }
  }, 20, this.destroyRef);


  protected override onLoaded(e) {
    super.onLoaded(e);
    this.zoneRedraw = false;
  }

  protected override onChartRender() {
    super.onChartRender();

    if (!this.zoneRedraw) 
      this.redrawZones();
  }

  protected override onPointSelect(point: Point, e: PointInteractionEventObject) {
    this.redrawZones();
    super.onPointSelect(point, e);
  }

  protected override onPointUnselect(point: Point, e: PointInteractionEventObject) {
    this.redrawZones();
    super.onPointUnselect(point, e);
  }

  computeXAxis() {
    let axis = super.computeXAxis();

    axis = {
      ...axis,
      // We do not want the axis scrollbar, as we use the plot area scrollbar instead
      scrollbar: { enabled: false },
      showEmpty: true,

      categories: this.computeCategorysForBarLikeChart()
    };

    return axis;
  }

  computeNavigatorOptions(): Highcharts.NavigatorOptions {
    let options = super.computeNavigatorOptions();

    return {
      ...options,
      // This appears to improve performance for large datasets in certesian charts.
      // I am not sure why that is exactly - I assume it prevents points being scanned multiple times when scrolling?
      adaptToUpdatedData: true,
    };
  }

  computeScrollablePlotArea() {
    let _ = [this.scrollViewSize(), this.totalSize()];

    let area = {
      scrollPositionX: 0,
      scrollPositionY: 0,
      minWidth:  1,
      minHeight: undefined,
      opacity:   0,
      isDirty:   true,
    };

    if (this.scrollActive()) {
      let minSize = this.totalSize();

      area.opacity   = 0.85;
      area.minWidth  = minSize;
      area.minHeight = undefined;
    }

    return area;
  }

  /**
   * Width of a point in the chart (space allocated for its tick).
   **/
  readonly pointSize = computed(() => {
    return FontSize.value(this.style()?.xAxis()?.fontSize, 12);
  });

  /**
   * Padding applied to a point on the left and right side.
   * This ensures markers do not overlap each other when fully squished.
   **/
  readonly pointPadding = computed(() => {
    return 2;
  });

  /**
   * Total space needed for plotting the chart.
   * This is the number of points x point size + point padding * 2 (for left/right of the point).
   **/
  readonly totalSize = computed(() => {
    let [pointSize, pointPadding] = [this.pointSize(), this.pointPadding()];
    let totalCategories = this.totalCategories();
    let pointTotalWidth = pointSize + pointPadding * 2;
    let width = totalCategories * pointTotalWidth;

    return Math.max(1, width);
  });


  /* Space available for the plot area. */
  readonly scrollViewSize = computed(() => {
    let size = this.chart()?.getContainerSize();
    return size.w;
  });

  /** Should scrollbar appear for the plot area? */
  readonly scrollActive = computed(() => {
    let availableSpace = this.scrollViewSize();
    let usedSpace      = this.totalSize();
    return usedSpace > availableSpace;
  });


  /** Computes total number of unique categories in this chart. */
  readonly totalCategories = computed(() => {
    if (!this.rows()?.hasResult() || !this.groupingFields()?.length)
      return 1;

    let grouping      = this.groupingFields()[0];
    let groupingIndex = this.rows().table.columns.get(grouping).dataIndex;
    let groupings     = this.rows().result().rows.map((row) => row[groupingIndex]);

    return filterToDistinct(groupings).length;
  });


  computeSharedTooltips() {
    return this.legendFields().length > 0;
  }

  override applyDefaultSorting(fields: VisualField[]) {
    let axiesFieldIndex = fields.findIndex((f) => f.role().type.title == VisualFieldRoleType.Defaults.groupingAxis.title);
    if (axiesFieldIndex != -1) {
      let field = fields[axiesFieldIndex]

      fields[axiesFieldIndex] = new VisualField(
        field.target,
        field.visualFieldId,
        <VisualFieldSortingInit>{
          sorted: true,
          sortDirection: SortDirection.Ascending,
          sortOrder: 0
        }
      );

      return fields;
    }

    return fields
  }

  
  override createMeasureSeries(series: CreateSeries): ChartSeries<SeriesLineOptions> {
    let s = super.createMeasureSeries(series) as ChartSeries<SeriesLineOptions>;

    return {
      ...s,
      marker: {
        states: {
          hover: {
            fillColor: (s as any).color,
            lineColor: (s as any).color,
            radiusPlus: 0,
          },

          select: {
            fillColor: (s as any).color,
            lineColor: (s as any).color,
            radiusPlus: 0,
          },
        }
      }
    };
  }
}

export namespace LineChartVisual {
  export class State extends ChartVisual.State { }
  export class Style extends ChartVisual.Style { }

  export interface Options extends ChartVisual.Options {
  }

  export const roles = VisualFieldRoleTypeMap.create({
    axis: new VisualFieldRoleType('axis', {
      ...VisualFieldRoleType.Defaults.groupingAxis
    }),

    measures: new VisualFieldRoleType('measures', {
      ...VisualFieldRoleType.Defaults.measures,
      limits: v =>
        (v.fieldRoles.get(roles.legend).fields.length > 0)
          ? { min: 1, max: 1 }
          : { min: 1 }
    }),

    legend: new VisualFieldRoleType('legend', {
      ...VisualFieldRoleType.Defaults.detailLegend,
      isEnabled: v => v.fieldRoles.get(roles.measures).fields.length <= 1,
      limits: v => ({ min: 0, max: 1 })
    }),
  });

  export const type = ChartVisual.defineType<LineChartVisualComponent>(
    LineChartVisualComponent,
    'lineChart',
    roles,
    {
      visualOptions: {
        dataLabelPosition: ChartVisual.DataLabelsPosition.Outside,

        legendEnabledByDefault: false,
        dataLabelsEnabledByDefault: false,
      },
      createState: () => new State(),
      createStyle: () => new Style(),
      showOverlayUntilRendered: true,
      getPbiChartType: v => PbiChartTypes.lineChart
    });
}
